import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import { CLIENT_CREATE_QUERY_KEY } from '@/app/(main)/clients/create/const/const';
import { useDeleteClientProfileImageMutation } from '@/app/(main)/clients/create/hooks/useDeleteClientProfileImageMutation';
import { primaryPassengerFirstNameField, primaryPassengerLastNameField, primaryPassengerMiddleNameField } from '@/app/(main)/clients/create/utils/validationSchema';
import { useEditClientMutation } from '@/app/(main)/clients/hooks/useEditClientMutation';
import type { CreateClientFormData } from '@/types/clientsApiTypes';
import { emailValidation, nullableString, validatePhoneNumber } from '@/utils/validations';
import { CLIENT_DETAILS_QUERY_KEY, CLIENT_QUERY_KEY } from '../../../consts/client-detail';
import { EditBasicInformationForm } from '../../EditSectionModals/components/EditBasicInformationForm';
import { ModalKeys } from '../../EditSectionModals/types';
import { defineSection } from '../../EditSectionModals/utils/defineSection';
import { getBasicInfoPayload } from '../../EditSectionModals/utils/getBasicInfoPayload';
import type { PayloadWithIsImageDeleted } from '../types';

const schema = yup.object({
  primary_passenger: yup.object().shape({
    client: yup.object().shape({
      userHasCertifiedTheInformation: yup
        .boolean()
        .oneOf([true, false], 'Please certify the information to proceed')
        .optional()
        .default(false),
      preferred_name: nullableString().defined(),
      is_image_deleted: yup.boolean().optional().default(false),
      profile_picture_gallery_id: yup.number().nullable().optional(),
      profile_picture_url: nullableString().optional(),
      first_name: primaryPassengerFirstNameField(),
      middle_name: primaryPassengerMiddleNameField(),
      last_name: primaryPassengerLastNameField(),
      gender: nullableString().defined().optional(),
      email: yup
        .string()
        .email('Invalid email')
        .test('is-valid-email', 'Invalid email', (value) => {
          return emailValidation(value ?? '');
        })
        .required('Email is required'),
      additional_emails: yup
        .array()
        .of(
          yup.object({
            email: yup
              .string()
              .email('Invalid email')
              .test('is-valid-email', 'Invalid email', (value) => {
                return emailValidation(value ?? '');
              })
              .required('Email is required'),
          })
        )
        .required()
        .default([]),
      date_of_birth: nullableString().optional(),
      phone: nullableString()
        .optional()
        .test('valid-phone-format', 'Invalid phone number', (value) => {
          if ((value?.length ?? 0) <= 3) return true; // skip validation when phone number include only country code
          return validatePhoneNumber(value);
        }),
      phones: yup
        .array()
        .of(
          yup.object({
            phone: yup
              .string()
              .optional()
              .defined()
              .test(
                'valid-phone-format',
                'Invalid phone number',
                (value) => !value || value.trim() === '' || validatePhoneNumber(value)
              ),
            title: yup.string().nullable().defined(),
          })
        )
        .required()
        .default([]),
      preferred_airport_ids: yup.array().of(yup.number().nullable()).optional().default([]),
    }),
  }),
});

export type BasicInfoFormValues = yup.InferType<typeof schema>;

const BasicInfoFormSection = () => {
  return <EditBasicInformationForm isPrimaryPassenger />;
};

function useBasicInfoOnSave() {
  const mutation = useEditClientMutation();
  const deleteProfileImageMutation = useDeleteClientProfileImageMutation();
  const queryClient = useQueryClient();

  return async (payload: PayloadWithIsImageDeleted) => {
    const { is_image_deleted, id, ...clientRest } = payload.client ?? {};
    if (!payload.client?.profile_picture_gallery_id && payload.client?.is_image_deleted) {
      await deleteProfileImageMutation.mutateAsync({
        clientId: Number(id),
      });
    }

    await mutation.mutateAsync(
      {
        data: {
          client: clientRest,
        },
        clientId: Number(id),
      },
      {
        onSuccess: () => {
          toast.success('Client updated successfully');
          queryClient.invalidateQueries({ queryKey: [CLIENT_DETAILS_QUERY_KEY] });
          queryClient.invalidateQueries({ queryKey: [CLIENT_CREATE_QUERY_KEY] });
          queryClient.invalidateQueries({ queryKey: [CLIENT_QUERY_KEY] });
        },
      }
    );
  };
}

export const basicInfoSection = defineSection<
  (typeof ModalKeys)['basic_info'],
  CreateClientFormData,
  typeof schema,
  PayloadWithIsImageDeleted
>({
  key: ModalKeys.basic_info,
  title: 'Basic information',
  subtitle: 'Core client details used for identification and communication.',
  schema,
  mapClientToValues: (c) => ({
    primary_passenger: {
      client: { ...c.primary_passenger.client, is_image_deleted: false },
      selected_airports: c.primary_passenger.selected_airports ?? [],
    },
  }),
  mapValuesToPayload: (v, client): PayloadWithIsImageDeleted => ({
    client: getBasicInfoPayload({
      client_id: client?.primary_passenger.client.client_id ?? 0,
      ...v.primary_passenger.client,
      preferred_airport_ids: (v.primary_passenger.client.preferred_airport_ids ?? []).filter(
        (id): id is number => typeof id === 'number'
      ),
      is_image_deleted: v?.primary_passenger.client?.is_image_deleted ?? false,
      crm_emails: client?.primary_passenger.client.crm_emails,
      crm_phones: client?.primary_passenger.client.crm_phones,
    }),
  }),
  useOnSave: useBasicInfoOnSave,
  Fields: BasicInfoFormSection,
});